<template>
  <div>
    <v-row
      @click="toggleExpand"
      no-gutters
      class="text-body-1 align-center px-1"
      style="flex-wrap: nowrap; min-width: 300px"
      :class="{ notActualItem: !isWithinActualTime, starredItem: starred }"
      v-touch="{
        left: () => removeStarred(),
        right: () => addStarred()
      }"
    >
      <v-col>
        <v-row
          no-gutters
          class="align-start"
        >
          <event-icon v-model="value.eventType" />
          <span
            style="font-size: 8pt; line-height: 10pt"
            class="font-weight-bold"
          >
            {{ value.flight.legState }}
          </span>
        </v-row>
        <v-row no-gutters>
          <v-col class="text-body-2">
            <v-badge
              :color="diffColor"
              v-if="timeDiff !== 0"
              :content="timeDiffPrinted(timeDiff)"
            >
              {{ time | localMoment('HH:mm') }}
            </v-badge>
            <span v-else>{{ time | localMoment('HH:mm') }}</span>
          </v-col>
        </v-row>
      </v-col>
      <v-col>
        <v-row
          no-gutters
          class="d-flex align-center"
        >
          <v-col
            cols="12"
            sm="6"
            class="text-h6"
          >
            {{ value.flight.number }}
            <span
              v-if="value.flight.suffix"
              class="warning--text"
            >
              {{ value.flight.suffix }}
            </span>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="text-body-2"
          >
            {{ value.flight.actualDepApIata }} -
            {{ value.flight.actualArrApIata }}
          </v-col>
        </v-row>
      </v-col>
      <v-col>
        <v-row
          no-gutters
          class="d-flex align-center"
        >
          <v-col
            cols="12"
            sm="6"
          >
            <v-icon
              small
              color="secondary"
            >
              mdi-airplane
            </v-icon>
            <span class="font-weight-medium body-2">{{ value.flight.aircraft }} </span>
          </v-col>
          <v-col
            cols="6"
            sm="3"
            class="text-body-2"
          >
            <v-icon
              small
              color="info"
              class="hidden-xs-only"
            >
              mdi-map-marker-outline
            </v-icon>
            <span class="text-caption text-sm-body-2">
              <template v-if="stand">
                {{ stand }}
              </template>
              <template v-else> - </template>
            </span>
          </v-col>
          <v-col
            cols="6"
            sm="3"
            class="text-body-2"
          >
            <v-icon
              small
              color="info"
              class="hidden-xs-only"
            >
              mdi-import
            </v-icon>
            <span class="text-caption text-sm-body-2">
              <template v-if="gate">
                {{ gate }}
              </template>
              <template v-else> - </template>
            </span>
          </v-col>
        </v-row>
      </v-col>

      <v-col>
        <v-row no-gutters>
          <v-col
            cols="5"
            sm="3"
            class="d-flex align-center"
          >
            <template v-if="value.flight.pax">
              <v-icon
                small
                color="info"
                class="hidden-xs-only"
              >
                mdi-human-male-female
              </v-icon>
              <span class="text-caption">{{ value.flight.pax.total }}</span>
            </template>
          </v-col>
          <v-col
            cols="7"
            sm="3"
            class="d-flex align-center"
          >
            <span
              v-if="isLdsVisible"
              class="text-caption"
              :class="{
                'success--text': isLdsAccepted,
                'font-weight-black': isLdsAccepted
              }"
            >
              LDS
            </span>
            <span
              v-if="isLdmVisible"
              class="text-caption font-weight-black accent--text ml-1"
            >
              LDM
            </span>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="text-body-1"
          >
            {{ value.flight.state }}
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-expand-transition>
      <div v-show="expandedId === value.flight.id">
        <v-divider></v-divider>
        <v-row no-gutters>
          <v-col>
            <v-btn
              icon
              @click="toggleStarred(value)"
            >
              <v-icon :color="starred ? 'success' : ''">star</v-icon></v-btn
            >
            <v-btn
              icon
              @click="moreInfo()"
            >
              <v-icon>info</v-icon>
            </v-btn>
            <v-btn
              icon
              @click="showConnections()"
            >
              <v-icon>settings_input_composite</v-icon>
            </v-btn>
            <v-btn
              x-small
              outlined
              v-if="isLdmVisible"
              @click="showLdm()"
            >
              LDM
            </v-btn>
            <v-btn
              @click="navigateFlightRadar()"
              right
              absolute
              icon
            >
              <f-r-image />
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
  import EventIcon from '@/components/EventIcon'
  import FRImage from '@/components/FRImage'
  import moment from 'moment'
  import { eventTypes } from '../config/eventTypes'

  export default {
    name: 'DepartureAndArrivalItem',
    components: { FRImage, EventIcon },
    props: {
      value: {
        type: Object,
        required: true
      },
      expandedId: {
        type: Number,
        required: false,
        default: 0
      },
      starred: {
        type: Boolean,
        required: false,
        default: false
      },
      now: {
        type: Date,
        required: true
      }
    },
    data: () => ({}),
    computed: {
      icon() {
        return this.value.eventType === eventTypes.arrival ? 'south_east' : 'north_east'
      },
      time() {
        return this.value.eventType === eventTypes.arrival
          ? this.value.flight.timeArrival
          : this.value.flight.timeDeparture
      },
      timeDiff() {
        return this.value.eventType === eventTypes.arrival
          ? this.value.flight.arrDiffMinutes
          : this.value.flight.depDiffMinutes
      },
      diffColor() {
        return this.timeDiff < 5 ? 'secondary' : this.timeDiff > 20 ? 'error' : 'warning'
      },
      stand() {
        return this.value.eventType === eventTypes.arrival
          ? this.value.flight.arrivalStand
          : this.value.flight.departureStand
      },
      gate() {
        return this.value.eventType === eventTypes.arrival
          ? this.value.flight.arrivalGate
          : this.value.flight.boardingGate
      },
      isLdsVisible() {
        return this.value.flight.loadsheetStatus !== undefined
      },
      isLdsAccepted() {
        return this.value.flight.loadsheetStatus === 'ACCEPTED'
      },
      isLdmVisible() {
        return this.value.flight.hasLdm === true
      },
      diff() {
        return -moment.utc(this.time).diff(moment(this.now), 'minutes')
      },
      isWithinActualTime() {
        const diff = this.diff
        return this.value.eventType === eventTypes.arrival
          ? diff > -30 && diff < 30
          : diff > -45 && diff < 15
      }
    },
    methods: {
      timeDiffPrinted(diff) {
        return diff === 0 ? '' : diff > 0 ? '+' + diff : diff
      },
      toggleExpand() {
        this.$emit('toggle', this.value.flight.id)
      },
      navigateFlightRadar() {
        const url =
          'http://www.flightradar24.com/data/aircraft/' + this.value.flight.aircraft
        window.open(url).focus()
      },
      toggleStarred() {
        this.$emit('toggleStarred')
      },
      addStarred() {
        if (!this.starred) this.toggleStarred()
      },
      removeStarred() {
        if (this.starred) this.toggleStarred()
      },
      moreInfo() {
        this.$emit('moreInfo', 'general')
      },
      showLdm() {
        this.$emit('moreInfo', 'ldm')
      },
      showConnections() {
        this.$emit('moreInfo', 'connections')
      }
    }
  }
</script>

<style>
  .starredItem {
    border-left: 5px solid var(--v-success-base);
  }
  .notActualItem {
    opacity: 0.4;
  }
</style>
